import { Show, createSignal, type Component, For } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { formatCell } from "@/shared/format-utils";
import { registerPanel } from "@/panel-registry";

interface SmbShare {
  Name?: string;
  Path?: string;
  ShareType?: string;
  Description?: string;
  FolderEnumerationMode?: string;
  CurrentUsers?: number;
}

interface SmbSession {
  SessionId?: number;
  ClientComputerName?: string;
  ClientUserName?: string;
  NumOpens?: number;
  SessionTime?: string;
}

interface SmbOpenFile {
  FileId?: number;
  SessionId?: number;
  ClientComputerName?: string;
  ClientUserName?: string;
  Path?: string;
}

interface DfsNamespace {
  Path?: string;
  Type?: string;
  State?: string;
}

interface DfsFolder {
  Path?: string;
  State?: string;
  Description?: string;
}

interface ListResult<T> {
  rows: T[];
  unavailable?: boolean;
}

interface ShareGetResult {
  shareName?: string;
  row?: Record<string, unknown> | null;
}

const SmbPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [selectedNamespace, setSelectedNamespace] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [aclDetails, setAclDetails] = createSignal<ShareGetResult | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const sharesQuery = createToolQuery<ListResult<SmbShare>>(() => ({
    tool: "smb_shares_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const sessionsQuery = createToolQuery<ListResult<SmbSession>>(() => ({
    tool: "smb_sessions_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const openFilesQuery = createToolQuery<ListResult<SmbOpenFile>>(() => ({
    tool: "smb_openfiles_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const namespacesQuery = createToolQuery<ListResult<DfsNamespace>>(() => ({
    tool: "dfs_namespaces_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const foldersQuery = createToolQuery<ListResult<DfsFolder>>(() => ({
    tool: "dfs_folders_list",
    params: { namespacePath: selectedNamespace() },
    enabled: enabled() && selectedNamespace() !== "",
    refetchInterval: 0,
  }));

  async function viewAcl(shareName: string): Promise<void> {
    if (!shareName) return;
    setActionStatus({ text: `Chargement ACL ${shareName}...`, level: "info" });
    try {
      const result = await tools.call<ShareGetResult>("smb_share_get", { shareName });
      setAclDetails(result);
      setActionStatus({ text: "ACL chargee.", level: "info" });
    } catch (err) {
      setActionStatus({
        text: `Erreur ACL: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function removeShare(shareName: string): Promise<void> {
    if (!shareName) return;
    const ok = await confirm.confirm({
      title: `Supprimer partage ${shareName}`,
      body: `Confirmer la suppression du partage SMB "${shareName}" ?`,
      level: "danger",
      confirmLabel: "Supprimer",
      entityName: shareName,
    });
    if (!ok) return;
    setActionStatus({ text: `Suppression ${shareName}...`, level: "info" });
    try {
      await tools.call("smb_share_remove", { shareName }, { userConfirmedAt: new Date().toISOString() });
      setActionStatus({ text: `Suppression ${shareName} reussie.`, level: "info" });
      await sharesQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function closeSession(sessionId: number | undefined, user: string): Promise<void> {
    if (sessionId == null) return;
    const ok = await confirm.confirm({
      title: `Fermer session ${sessionId}`,
      body: `Confirmer la fermeture de la session SMB ${sessionId} (${user || "?"}) ?`,
      level: "danger",
      confirmLabel: "Fermer",
      entityName: String(sessionId),
    });
    if (!ok) return;
    setActionStatus({ text: `Fermeture session ${sessionId}...`, level: "info" });
    try {
      await tools.call(
        "smb_session_close",
        { sessionId: Number(sessionId) },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: "Fermeture session reussie.", level: "info" });
      await sessionsQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const shareColumns: Column<SmbShare>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "Path", header: "Path" },
    { accessorKey: "ShareType", header: "ShareType" },
    { accessorKey: "Description", header: "Description" },
    { accessorKey: "FolderEnumerationMode", header: "FolderEnumerationMode" },
    { accessorKey: "CurrentUsers", header: "CurrentUsers" },
  ];

  const sessionColumns: Column<SmbSession>[] = [
    { accessorKey: "SessionId", header: "SessionId" },
    { accessorKey: "ClientComputerName", header: "ClientComputerName" },
    { accessorKey: "ClientUserName", header: "ClientUserName" },
    { accessorKey: "NumOpens", header: "NumOpens" },
    { accessorKey: "SessionTime", header: "SessionTime" },
  ];

  const openFileColumns: Column<SmbOpenFile>[] = [
    { accessorKey: "FileId", header: "FileId" },
    { accessorKey: "SessionId", header: "SessionId" },
    { accessorKey: "ClientComputerName", header: "ClientComputerName" },
    { accessorKey: "ClientUserName", header: "ClientUserName" },
    { accessorKey: "Path", header: "Path" },
  ];

  const namespaceColumns: Column<DfsNamespace>[] = [
    { accessorKey: "Path", header: "Path" },
    { accessorKey: "Type", header: "Type" },
    { accessorKey: "State", header: "State" },
  ];

  const folderColumns: Column<DfsFolder>[] = [
    { accessorKey: "Path", header: "Path" },
    { accessorKey: "State", header: "State" },
    { accessorKey: "Description", header: "Description" },
  ];

  const subtabs: SubTab[] = [
    {
      id: "shares",
      label: "Partages",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => sharesQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<SmbShare>
            rows={sharesQuery.data?.rows ?? []}
            columns={shareColumns}
            rowActions={(r) => (
              <>
                <button type="button" class="btn btn--xs btn--ghost" onClick={() => viewAcl(r.Name ?? "")}>
                  Voir ACL
                </button>
                <button type="button" class="btn btn--xs btn--danger" onClick={() => removeShare(r.Name ?? "")}>
                  Supprimer
                </button>
              </>
            )}
          />
          <Show when={aclDetails()}>
            {(d) => (
              <div class="panel-data-wrap" style={{ "margin-top": "8px" }}>
                <div class="eyebrow">Details partage: {d().shareName}</div>
                <pre
                  style={{
                    "white-space": "pre-wrap",
                    "word-break": "break-word",
                    "font-family": "Menlo, Consolas, monospace",
                    "font-size": "12px",
                    background: "#f7f7f9",
                    color: "#111",
                    padding: "12px",
                    "border-radius": "4px",
                    margin: "0",
                  }}
                >
                  {JSON.stringify(d().row ?? {}, null, 2)}
                </pre>
                <button type="button" class="btn btn--xs btn--ghost" onClick={() => setAclDetails(null)}>
                  Fermer
                </button>
              </div>
            )}
          </Show>
        </>
      ),
    },
    {
      id: "sessions",
      label: "Sessions",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => sessionsQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<SmbSession>
            rows={sessionsQuery.data?.rows ?? []}
            columns={sessionColumns}
            rowActions={(r) => (
              <button
                type="button"
                class="btn btn--xs btn--danger"
                onClick={() => closeSession(r.SessionId, r.ClientUserName ?? "")}
              >
                Fermer session
              </button>
            )}
          />
        </>
      ),
    },
    {
      id: "openfiles",
      label: "Fichiers ouverts",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => openFilesQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<SmbOpenFile> rows={openFilesQuery.data?.rows ?? []} columns={openFileColumns} />
        </>
      ),
    },
    {
      id: "dfs-ns",
      label: "DFS Namespaces",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => namespacesQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <Show
            when={!namespacesQuery.data?.unavailable}
            fallback={<div class="panel-data-empty">Module DFSN non installe sur cet hote.</div>}
          >
            <DataTable<DfsNamespace> rows={namespacesQuery.data?.rows ?? []} columns={namespaceColumns} />
          </Show>
        </>
      ),
    },
    {
      id: "dfs-folders",
      label: "DFS Folders",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <span class="panel-toolbar-label">Namespace:</span>
            <select
              class="panel-filter-input"
              value={selectedNamespace()}
              onChange={(e) => setSelectedNamespace(e.currentTarget.value)}
            >
              <option value="">-- choisir --</option>
              <For each={namespacesQuery.data?.rows ?? []}>
                {(n) => <option value={n.Path ?? ""}>{n.Path ?? ""}</option>}
              </For>
            </select>
            <button
              type="button"
              class="btn btn--xs btn--info"
              disabled={!selectedNamespace()}
              onClick={() => foldersQuery.refetch()}
            >
              Rafraichir
            </button>
          </div>
          <DataTable<DfsFolder> rows={foldersQuery.data?.rows ?? []} columns={folderColumns} />
        </>
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (
      sharesQuery.isFetching ||
      sessionsQuery.isFetching ||
      openFilesQuery.isFetching ||
      namespacesQuery.isFetching ||
      foldersQuery.isFetching
    )
      return { text: "Chargement...", level: "info" };
    return undefined;
  };

  // référence formatCell pour éviter warning unused
  void formatCell;

  return <PanelLayout title="Partages SMB / DFS" subtabs={subtabs} status={status()} />;
};

registerPanel("smb", SmbPanel);
export default SmbPanel;
